import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  History, Search, Loader2, AlertTriangle, CheckCircle, XCircle,
  ChevronDown, ChevronUp, Mail, Phone, Hash, User, ShieldAlert,
  Building2, Calendar, LogIn, LogOut, MessageSquareWarning, Shield,
  FileText, ArrowLeftRight,
} from 'lucide-react';
import { authAPI, isPlatformAdmin } from '../lib/auth';

// ─── Constants ───────────────────────────────────────────────────────────────

const RISK_COLORS = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high:     'bg-orange-100 text-orange-800 border-orange-200',
  medium:   'bg-yellow-100 text-yellow-800 border-yellow-200',
  low:      'bg-green-100 text-green-800 border-green-200',
};

const FLAG_LABELS = {
  has_high_risk_content: { label: 'High-Risk Content',   color: 'bg-red-50 text-red-700 border-red-200' },
  has_mcc_risk:          { label: 'MCC Misalignment',    color: 'bg-orange-50 text-orange-700 border-orange-200' },
  has_payment_risk:      { label: 'Payment Risk',        color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  has_mule_shell:        { label: 'Mule / Shell Entity', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  has_credit_risk:       { label: 'Credit Risk',         color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
}

/** Derive source label for a record if the backend didn't compute it. */
function deriveSourceLabel(rec, isAdmin) {
  if (rec.source_label) return rec.source_label;
  const hasOwn = (rec.tenant_records || []).some(tr => tr.is_own);
  if (isAdmin) return rec.first_tenant_name || 'Unknown';
  return hasOwn ? 'Your Record' : 'Bank/PSP';
}

// ─── Sub-components ──────────────────────────────────────────────────────────

/**
 * SOURCE badge for the table column.
 * - Tenant users: "Your Record" (purple) or "Bank/PSP" (gray)
 * - Platform admins: actual tenant name in blue
 */
function SourceBadge({ label, isAdmin }) {
  if (label === 'Your Record')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
        <Shield className="w-3 h-3" /> Your Record
      </span>
    );
  if (isAdmin && label && label !== 'Bank/PSP' && label !== 'Unknown')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
        <Building2 className="w-3 h-3" /> {label}
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
      <Building2 className="w-3 h-3" /> Bank/PSP
    </span>
  );
}

/**
 * Inline Bank/PSP label inside the expanded detail panel.
 * - isOwn → "Your Record"
 * - not own, isAdmin → real tenant name
 * - not own, not admin → "Bank/PSP"
 */
function BankPSPLabel({ name, isOwn, isAdmin }) {
  const label = isOwn ? 'Your Record' : (isAdmin && name ? name : 'Bank/PSP');
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${isOwn ? 'text-purple-700' : 'text-gray-600'}`}>
      <Building2 className="w-3 h-3 flex-shrink-0" />
      {label}
      {isOwn && <span className="text-[9px] bg-purple-100 text-purple-600 px-1 rounded ml-0.5">You</span>}
    </span>
  );
}

/** Group tenant_records by tenant_id so we show one entry per Bank/PSP. */
function groupByTenant(records) {
  const map = {};
  for (const tr of records) {
    const key = tr.tenant_id || '__null__';
    if (!map[key]) map[key] = { tenant_name: tr.tenant_name, tenant_id: tr.tenant_id, is_own: tr.is_own, actions: [] };
    map[key].actions.push(tr);
  }
  return Object.values(map).sort((a, b) => (b.is_own ? 1 : 0) - (a.is_own ? 1 : 0));
}

/** Expanded Bank/PSP history panel shown inside a table row. */
function BankPSPHistory({ records, isAdmin }) {
  const [expanded, setExpanded] = useState(false);
  if (!records || records.length === 0)
    return <p className="text-xs text-gray-400 italic">No Bank/PSP history recorded yet.</p>;

  const groups = groupByTenant(records);
  const shown  = expanded ? groups : groups.slice(0, 3);

  return (
    <div className="space-y-2">
      {shown.map((g, i) => {
        const onboarded  = g.actions.find(a => a.action === 'onboarded');
        const deboarded  = [...g.actions].reverse().find(a => a.action === 'de-boarded');
        const remarks    = g.actions.filter(a => a.notes).map(a => a.notes);
        const latest     = g.actions[g.actions.length - 1];
        return (
          <div key={i} className={`rounded-lg border p-3 text-[11px] space-y-2 ${
            g.is_own ? 'border-purple-200 bg-purple-50' : 'border-gray-100 bg-gray-50'
          }`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <BankPSPLabel name={g.tenant_name} isOwn={g.is_own} isAdmin={isAdmin} />
              <span className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                deboarded ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
              }`}>
                {deboarded
                  ? <><LogOut className="w-2.5 h-2.5 inline" /> De-boarded</>
                  : <><LogIn  className="w-2.5 h-2.5 inline" /> Active</>}
              </span>
            </div>

            <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-gray-500">
              {onboarded && (
                <span className="flex items-center gap-1">
                  <LogIn className="w-2.5 h-2.5 text-green-500" />
                  Onboarded: <strong className="text-gray-700 ml-0.5">{fmtDate(onboarded.performed_at)}</strong>
                </span>
              )}
              {deboarded && (
                <span className="flex items-center gap-1">
                  <LogOut className="w-2.5 h-2.5 text-red-500" />
                  De-boarded: <strong className="text-red-600 ml-0.5">{fmtDate(deboarded.performed_at)}</strong>
                </span>
              )}
              {!onboarded && latest && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-2.5 h-2.5" />
                  Last activity: {fmtDate(latest.performed_at)}
                </span>
              )}
            </div>

            {remarks.length > 0 && (
              <div className="border-t border-gray-200 pt-1.5 space-y-1">
                {remarks.map((note, j) => (
                  <div key={j} className="flex items-start gap-1 text-gray-600">
                    <MessageSquareWarning className="w-2.5 h-2.5 flex-shrink-0 mt-0.5 text-orange-400" />
                    <span className="leading-tight">{note}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {groups.length > 3 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-[10px] text-purple-600 hover:underline flex items-center gap-0.5"
        >
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {expanded ? 'Show less' : `+${groups.length - 3} more Bank/PSP`}
        </button>
      )}
    </div>
  );
}

/** MCC match badge (aligned with Reports Validation column). */
function MccMatchBadge({ status, bankMcc, matchLabel }) {
  const vs = (status || '').toLowerCase();
  if (vs === 'match') {
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-50 text-green-700 border border-green-200">
        <CheckCircle className="w-2.5 h-2.5" /> MCC Match{bankMcc ? ` (${bankMcc})` : ''}
      </span>
    );
  }
  if (vs === 'mismatch') {
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200">
        <AlertTriangle className="w-2.5 h-2.5" /> MCC Mismatch{bankMcc ? ` · bank ${bankMcc}` : ''}
      </span>
    );
  }
  if (matchLabel) {
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-50 text-slate-600 border border-slate-200">
        Match: {matchLabel}
      </span>
    );
  }
  return null;
}

/** Side-by-side previous vs current MCC when merchant was classified more than once. */
function MccChangePanel({ change, compact = false }) {
  if (!change || (!change.mcc_changed && !change.has_multiple_runs)) return null;

  const prev = change.previous;
  const cur = change.current;

  if (change.mcc_changed && prev && cur) {
    const body = (
      <div className={`space-y-2 ${compact ? 'text-[10px]' : 'text-xs'}`}>
        <div className="flex items-center gap-1.5 font-semibold text-amber-800">
          <ArrowLeftRight className="w-3.5 h-3.5 shrink-0" />
          MCC changed ({change.run_count} run{change.run_count !== 1 ? 's' : ''})
        </div>
        <div className={`grid gap-2 ${compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
          <div className="rounded-lg border border-gray-200 bg-white p-2.5 space-y-1">
            <div className="text-[10px] font-semibold text-gray-500 uppercase">Previous</div>
            <div className="font-mono font-bold text-gray-800">{prev.mcc_code || '—'}</div>
            <div className="text-gray-600">{prev.mcc_description || '—'}</div>
            {prev.business_summary && (
              <p className="text-gray-500 leading-snug"><span className="font-medium text-gray-600">Business:</span> {prev.business_summary}</p>
            )}
            {prev.reasoning_short && (
              <p className="text-gray-500 leading-snug"><span className="font-medium text-gray-600">Reasoning:</span> {prev.reasoning_short}</p>
            )}
            {prev.analyzed_at && <p className="text-[9px] text-gray-400">{fmtDate(prev.analyzed_at)}</p>}
          </div>
          <div className="rounded-lg border border-green-200 bg-green-50/50 p-2.5 space-y-1">
            <div className="text-[10px] font-semibold text-green-700 uppercase">Current</div>
            <div className="font-mono font-bold text-green-900">{cur.mcc_code || '—'}</div>
            <div className="text-green-800">{cur.mcc_description || '—'}</div>
            {cur.business_summary && (
              <p className="text-green-900/80 leading-snug"><span className="font-medium">Business:</span> {cur.business_summary}</p>
            )}
            {cur.reasoning_short && (
              <p className="text-green-900/80 leading-snug"><span className="font-medium">Reasoning:</span> {cur.reasoning_short}</p>
            )}
            {cur.analyzed_at && <p className="text-[9px] text-green-700/70">{fmtDate(cur.analyzed_at)}</p>}
          </div>
        </div>
        {change.why_changed && (
          <p className="text-gray-600 leading-snug border-l-2 border-amber-300 pl-2">{change.why_changed}</p>
        )}
      </div>
    );
    return body;
  }

  if (change.has_multiple_runs) {
    return (
      <p className="text-[10px] text-gray-500">
        Classified {change.run_count} times — primary MCC unchanged ({cur?.mcc_code || prev?.mcc_code || '—'}).
      </p>
    );
  }
  return null;
}

/** Compact merchant cell: MCC code, short report summary, match, UW High/Critical. */
function MerchantSummaryCell({ r }) {
  const report = r.mcc_report || {};
  const change = r.mcc_change;
  const mccCode = report.primary_mcc_code || r.mcc_code;
  const mccDesc = report.primary_mcc_description || r.mcc_description;
  const summary = report.summary_short || '';
  const elevated = (r.underwriting_risk_history || []).filter(
    (p) => ['high', 'critical'].includes((p.risk_level || '').toLowerCase())
  );

  return (
    <div className="w-full min-w-0 space-y-1.5">
      <div className="font-medium text-gray-900 truncate">{r.merchant_name || '—'}</div>

      {/* URL left + High MCC right on the same line; MCC fills remaining width */}
      <div className="flex items-start gap-4 w-full min-w-0">
        <div className="text-xs text-blue-500 truncate shrink-0 max-w-[240px] pt-0.5">{r.url}</div>

        {elevated.length > 0 && (
          <div className="flex-1 min-w-0 space-y-1.5">
            {elevated.slice(0, 2).map((p, i) => (
              <div
                key={i}
                className={`rounded-md border px-2.5 py-1.5 text-[10px] leading-snug w-full ${
                  (p.risk_level || '').toLowerCase() === 'critical'
                    ? 'bg-red-50 border-red-200 text-red-800'
                    : 'bg-orange-50 border-orange-200 text-orange-800'
                }`}
              >
                <div className="flex items-center justify-between gap-3 font-semibold">
                  <span className="truncate">
                    {(p.risk_level || '').charAt(0).toUpperCase() + (p.risk_level || '').slice(1)}
                    {p.mcc_code ? ` · MCC ${p.mcc_code}` : ''}
                  </span>
                  <span className="font-normal text-[9px] opacity-80 whitespace-nowrap shrink-0">
                    {p.source_label || (p.source === 'underwriting' ? 'Underwriting' : 'MCC')}
                    {p.analyzed_at ? ` · ${fmtDate(p.analyzed_at)}` : ''}
                  </span>
                </div>
                {p.summary_short && (
                  <p className="mt-0.5 line-clamp-2 font-normal opacity-90">{p.summary_short}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {(mccCode || mccDesc) && (
        <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
          {mccCode && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-800 border border-green-200 font-mono">
              {mccCode}
            </span>
          )}
          {mccDesc && (
            <span className="text-[11px] text-gray-600 leading-snug line-clamp-2">
              {mccDesc}
            </span>
          )}
        </div>
      )}

      {summary && !change?.mcc_changed && (
        <p className="text-[11px] text-gray-500 leading-snug line-clamp-3 border-l-2 border-blue-200 pl-2">
          {summary}
        </p>
      )}

      <MccChangePanel change={change} compact />

      <div className="flex flex-wrap gap-1">
        <MccMatchBadge
          status={report.mcc_validation_status}
          bankMcc={report.bank_provided_mcc}
          matchLabel={report.mcc_match_label}
        />
        {r.has_past_elevated_risk && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-700 border border-red-200">
            <ShieldAlert className="w-2.5 h-2.5" /> Past High/Critical
          </span>
        )}
        {change?.mcc_changed && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200">
            <ArrowLeftRight className="w-2.5 h-2.5" /> MCC Changed
          </span>
        )}
      </div>
    </div>
  );
}

/** Expandable table row detail (MCC report + high-risk flags + Bank/PSP history). */
function ExpandedRow({ r, isAdmin, colSpan }) {
  const flags = Object.entries(FLAG_LABELS).filter(([key]) => r[key]);
  const report = r.mcc_report || {};
  const elevated = (r.underwriting_risk_history || []).filter(
    (p) => ['high', 'critical'].includes((p.risk_level || '').toLowerCase())
  );
  return (
    <tr className="bg-gray-50 border-b border-gray-100">
      <td colSpan={colSpan} className="px-6 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* MCC Module report */}
          <div className="space-y-2">
            {elevated.length > 0 && (
              <div className="space-y-1.5">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                  <ShieldAlert className="w-3.5 h-3.5 text-red-400" /> Underwriting / Past High–Critical
                </h4>
                {elevated.map((p, i) => (
                  <div
                    key={i}
                    className={`rounded-lg border p-2.5 text-[11px] ${
                      (p.risk_level || '').toLowerCase() === 'critical'
                        ? 'border-red-200 bg-red-50 text-red-900'
                        : 'border-orange-200 bg-orange-50 text-orange-900'
                    }`}
                  >
                    <div className="font-semibold flex justify-between gap-2">
                      <span>
                        {(p.risk_level || '').charAt(0).toUpperCase() + (p.risk_level || '').slice(1)}
                        {p.mcc_code ? ` · ${p.mcc_code}` : ''}
                      </span>
                      <span className="font-normal text-[10px] opacity-80">
                        {p.source_label || 'MCC'} · {fmtDate(p.analyzed_at)}
                      </span>
                    </div>
                    {p.summary_short && <p className="mt-1 leading-snug opacity-90">{p.summary_short}</p>}
                  </div>
                ))}
              </div>
            )}

            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5 text-blue-500" /> MCC Report Summary
            </h4>
            {(report.primary_mcc_code || r.mcc_code) ? (
              <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-3 space-y-2 text-xs text-gray-700">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-mono font-semibold bg-green-100 text-green-800 border border-green-200">
                    {report.primary_mcc_code || r.mcc_code}
                  </span>
                  <span className="text-gray-600">
                    {report.primary_mcc_description || r.mcc_description || '—'}
                  </span>
                </div>
                <MccMatchBadge
                  status={report.mcc_validation_status}
                  bankMcc={report.bank_provided_mcc}
                  matchLabel={report.mcc_match_label}
                />
                {report.summary_short ? (
                  <p className="leading-relaxed text-gray-600">{report.summary_short}</p>
                ) : (
                  <p className="text-gray-400 italic">No classification summary available.</p>
                )}
                {report.analyzed_at && (
                  <p className="text-[10px] text-gray-400">Classified {fmtDate(report.analyzed_at)}</p>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-400">No MCC classification linked yet.</p>
            )}

            {r.mcc_change?.mcc_changed && (
              <div className="pt-2 border-t border-gray-200">
                <MccChangePanel change={r.mcc_change} />
              </div>
            )}
          </div>

          {/* High-risk findings */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
              <ShieldAlert className="w-3.5 h-3.5 text-red-400" /> High-Risk Findings
            </h4>
            {flags.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {flags.map(([key, { label, color }]) => (
                  <span key={key} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${color}`}>
                    <AlertTriangle className="w-2.5 h-2.5" /> {label}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400">No high-risk flags detected.</p>
            )}
            {/* Contact info */}
            {(r.merchant_email || r.merchant_phone || r.merchant_mid || r.director_name) && (
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                {r.merchant_email && <span><Mail className="w-3 h-3 inline mr-0.5" />{r.merchant_email}</span>}
                {r.merchant_phone && <span><Phone className="w-3 h-3 inline mr-0.5" />{r.merchant_phone}</span>}
                {r.merchant_mid   && <span><Hash className="w-3 h-3 inline mr-0.5" />MID: {r.merchant_mid}</span>}
                {r.director_name  && <span><User className="w-3 h-3 inline mr-0.5" />{r.director_name}</span>}
              </div>
            )}
          </div>

          {/* Bank/PSP history */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5 text-blue-400" /> Bank / PSP History
              {isAdmin && r.tenant_count > 0 && (
                <span className="ml-1 bg-gray-200 text-gray-500 text-[10px] px-1.5 rounded-full">{r.tenant_count}</span>
              )}
            </h4>
            <BankPSPHistory records={r.tenant_records} isAdmin={isAdmin} />
          </div>
        </div>
      </td>
    </tr>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function MerchantHistoryPage({ embedded = false, initialSearch = '' } = {}) {
  const [records, setRecords]           = useState([]);
  const [loading, setLoading]           = useState(false);
  const [hasSearched, setHasSearched]   = useState(!isPlatformAdmin() || !!initialSearch);
  const [search, setSearch]             = useState(initialSearch || '');
  const [riskFilter, setRiskFilter]     = useState('');
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');
  const [email, setEmail]               = useState('');
  const [phone, setPhone]               = useState('');
  const [mid, setMid]                   = useState('');
  const [director, setDirector]         = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError]               = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const isAdmin = isPlatformAdmin();
  const tenantMountLoadedRef = useRef(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (search)     params.q          = search;
      if (riskFilter) params.risk_level = riskFilter;
      if (dateFrom)   params.date_from  = dateFrom;
      if (dateTo)     params.date_to    = dateTo;
      if (email)      params.email      = email;
      if (phone)      params.phone      = phone;
      if (mid)        params.mid        = mid;
      if (director)   params.director   = director;
      const res = await authAPI.listRiskHistory(params);
      if (res.data.requires_search) { setRecords([]); return; }
      setRecords(res.data.records || []);
      setExpandedRows({});
    } catch {
      setError('Failed to load risk history. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [search, riskFilter, dateFrom, dateTo, email, phone, mid, director]);

  // Tenants: auto-load own records once on mount. Platform admins: search-gated (avoid dumping entire DB).
  useEffect(() => {
    if (initialSearch) {
      setHasSearched(true);
      void loadData();
      return;
    }
    if (isAdmin) return;
    if (tenantMountLoadedRef.current) return;
    tenantMountLoadedRef.current = true;
    void loadData();
  }, [isAdmin, loadData, initialSearch]);

  useEffect(() => {
    if (initialSearch && initialSearch !== search) {
      setSearch(initialSearch);
    }
  }, [initialSearch]);

  const handleSearch = async (e) => {
    e.preventDefault();
    setHasSearched(true);
    await loadData();
  };

  const toggleRow = (id) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));

  // Table column count changes based on isAdmin
  const colSpan = isAdmin ? 7 : 7;

  return (
    <div className={`space-y-6 ${embedded ? '' : 'px-1'}`}>
      {!embedded && (
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <History className="w-6 h-6 text-purple-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Merchant Risk History</h1>
              <p className="text-xs text-gray-500 mt-0.5">
                {isAdmin
                  ? 'Platform-wide cross-Bank/PSP risk intelligence — all tenants visible'
                  : 'Your merchant records — search to check cross-Bank/PSP onboarding status'}
              </p>
            </div>
          </div>
          {hasSearched && (
            <span className="text-sm text-gray-500">
              {records.length} record{records.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}
      {embedded && hasSearched && (
        <p className="text-sm text-gray-500">
          {records.length} record{records.length !== 1 ? 's' : ''} found
        </p>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      {/* ── Search form ── */}
      <form onSubmit={handleSearch} className="space-y-3">
        <div className="flex gap-3 flex-wrap items-center">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Merchant name or website URL…"
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <select
            value={riskFilter}
            onChange={e => setRiskFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">All Risk Levels</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <div className="flex items-center gap-1.5 px-2 py-1.5 border border-gray-200 rounded-lg bg-white shrink-0">
            <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="text-xs border-0 focus:outline-none focus:ring-0 text-gray-700 bg-transparent w-[118px]"
              title="From date"
              aria-label="From date"
            />
            <span className="text-gray-300 text-xs">–</span>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              min={dateFrom || undefined}
              className="text-xs border-0 focus:outline-none focus:ring-0 text-gray-700 bg-transparent w-[118px]"
              title="To date"
              aria-label="To date"
            />
            {(dateFrom || dateTo) && (
              <button
                type="button"
                onClick={() => { setDateFrom(''); setDateTo(''); }}
                className="text-[10px] text-gray-400 hover:text-gray-600 px-1"
                title="Clear dates"
              >
                Clear
              </button>
            )}
          </div>
          <button
            type="submit"
            className="px-5 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 flex items-center gap-2 shrink-0"
          >
            <Search className="w-3.5 h-3.5" /> Search
          </button>
          <button
            type="button"
            onClick={() => setShowAdvanced(v => !v)}
            className="flex items-center gap-1 px-3 py-2 text-gray-600 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Advanced
          </button>
        </div>

        {showAdvanced && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
            {[
              { label: 'Email',          Icon: Mail,  val: email,    set: setEmail,    ph: 'merchant@example.com' },
              { label: 'Phone',          Icon: Phone, val: phone,    set: setPhone,    ph: '+91 9876543210' },
              { label: 'MID',            Icon: Hash,  val: mid,      set: setMid,      ph: 'Merchant ID' },
              { label: 'Director / Owner', Icon: User, val: director, set: setDirector, ph: 'Director name' },
            ].map(({ label, Icon, val, set, ph }) => (
              <div key={label}>
                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-1">
                  <Icon className="w-3.5 h-3.5" /> {label}
                </label>
                <input
                  type="text"
                  value={val}
                  onChange={e => set(e.target.value)}
                  placeholder={ph}
                  className="w-full px-3 py-1.5 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            ))}
          </div>
        )}
      </form>

      {/* ── Loading ── */}
      {loading && (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      )}

      {/* ── Platform admin: prompt to search ── */}
      {!loading && !hasSearched && (
        <div className="flex flex-col items-center justify-center py-24 text-center space-y-3">
          <ShieldAlert className="w-14 h-14 text-gray-200" />
          <p className="text-base font-medium text-gray-500">Search to view risk history</p>
          <p className="text-sm text-gray-400 max-w-md">
            Enter a merchant name, website URL, email, phone, MID, or director name to find
            cross-Bank/PSP onboarding history, high-risk findings, and non-compliance remarks.
          </p>
        </div>
      )}

      {/* ── No results ── */}
      {!loading && hasSearched && records.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center space-y-2">
          <Search className="w-10 h-10 text-gray-200" />
          <p className="text-sm font-medium text-gray-500">No records found</p>
          <p className="text-xs text-gray-400">
            {search
              ? `"${search}" was not found. Try a different name, URL, email, or phone.`
              : 'No merchant risk records are available yet.'}
          </p>
        </div>
      )}

      {/* ── Results table ── */}
      {!loading && hasSearched && records.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wide w-[52%]">Merchant</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wide whitespace-nowrap">Risk</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wide whitespace-nowrap">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wide whitespace-nowrap">Source</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wide whitespace-nowrap">First Seen</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wide whitespace-nowrap">Last Updated</th>
                  <th className="py-3 px-4 w-10" />
                </tr>
              </thead>
              <tbody>
                {records.map(r => {
                  const sourceLabel = deriveSourceLabel(r, isAdmin);
                  const isExpanded  = !!expandedRows[r.id];
                  return (
                    <React.Fragment key={r.id}>
                      <tr
                        className={`border-b border-gray-100 transition-colors ${isExpanded ? 'bg-purple-50/40' : 'hover:bg-gray-50'} cursor-pointer`}
                        onClick={() => toggleRow(r.id)}
                      >
                        {/* Merchant */}
                        <td className="py-3 px-4 align-top w-[52%]">
                          <MerchantSummaryCell r={r} />
                        </td>

                        {/* Risk */}
                        <td className="py-3 px-4 align-top whitespace-nowrap">
                          {r.overall_risk_level ? (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${RISK_COLORS[r.overall_risk_level] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                              {r.overall_risk_level.charAt(0).toUpperCase() + r.overall_risk_level.slice(1)}
                            </span>
                          ) : <span className="text-gray-300">—</span>}
                        </td>

                        {/* Status */}
                        <td className="py-3 px-4 align-top whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            r.status === 'active'     ? 'bg-green-50 text-green-700' :
                            r.status === 'de-boarded' ? 'bg-red-50 text-red-700'   : 'bg-gray-100 text-gray-500'
                          }`}>
                            {r.status === 'active'     ? <CheckCircle className="w-3 h-3" /> :
                             r.status === 'de-boarded' ? <XCircle className="w-3 h-3" />    :
                                                         <AlertTriangle className="w-3 h-3" />}
                            {r.status || '—'}
                          </span>
                        </td>

                        {/* Source */}
                        <td className="py-3 px-4 align-top whitespace-nowrap">
                          <SourceBadge label={sourceLabel} isAdmin={isAdmin} />
                        </td>

                        {/* First seen */}
                        <td className="py-3 px-4 align-top text-xs text-gray-500 whitespace-nowrap">
                          {fmtDate(r.onboarded_at || r.created_at)}
                        </td>

                        {/* Last updated */}
                        <td className="py-3 px-4 align-top text-xs text-gray-500 whitespace-nowrap">
                          {fmtDate(r.updated_at)}
                        </td>

                        {/* Expand toggle */}
                        <td className="py-3 px-4 text-right">
                          <button
                            onClick={e => { e.stopPropagation(); toggleRow(r.id); }}
                            className="p-1.5 rounded-md hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors"
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </td>
                      </tr>

                      {/* Expanded detail row */}
                      {isExpanded && <ExpandedRow r={r} isAdmin={isAdmin} colSpan={colSpan} />}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


